/** The version of the open module, in the editor's header: a chip that opens
 *  into the module's whole history.
 *
 *  The closed label comes from `meta`, not from the list: the editor always
 *  knows what it has open, and the header must not wait on a second request. */
import { useEffect, useRef, useState } from "react";
import * as api from "../../../api";
import { dataOra, useT } from "../../../i18n";
import { potPleca } from "../../../nescris";

type Stare = api.Tronson["status"];

const STARI: Record<Stare, { text: "editor.publicat" | "editor.ciorna"
                                   | "editor.versiuni.inlocuita"; chip: string }> = {
  published: { text: "editor.publicat", chip: "ob-c-ok" },
  draft: { text: "editor.ciorna", chip: "ob-c-amb" },
  retired: { text: "editor.versiuni.inlocuita", chip: "ob-c-mut" },
};

/** Published first: it is what plans use, wherever it sits in the history. */
function inOrdine(versiuni: api.VersiuneTronson[]) {
  return [...versiuni.filter((v) => v.status === "published"),
          ...versiuni.filter((v) => v.status !== "published")];
}

export default function VersionPicker({
  code, projectId, meta, reimprospatare, dezactivat, onChoose,
}: {
  code: string;
  projectId?: string;
  meta: Pick<api.Tronson, "version" | "status" | "scope">;
  /** A new value reads the list again: after a save, a publish and a load. */
  reimprospatare: number;
  dezactivat: boolean;
  onChoose: (versiune: number) => void;
}) {
  const t = useT();
  const [deschis, setDeschis] = useState(false);
  const [lista, setLista] = useState<api.VersiuneTronson[]>([]);
  const [eroare, setEroare] = useState(false);
  const cutie = useRef<HTMLDivElement>(null);
  const global = meta.scope === "global";

  useEffect(() => {
    if (global) return undefined;
    let viu = true;
    api.versiuniTronson(code, projectId)
      .then((r) => { if (viu) { setLista(r.versions); setEroare(false); } })
      .catch(() => { if (viu) setEroare(true); });
    return () => { viu = false; };
  }, [code, projectId, global, reimprospatare]);

  useEffect(() => {
    if (!deschis) return undefined;
    const afara = (e: MouseEvent) => {
      if (!cutie.current?.contains(e.target as Node)) setDeschis(false);
    };
    document.addEventListener("mousedown", afara);
    return () => document.removeEventListener("mousedown", afara);
  }, [deschis]);

  if (global) return <span className="ob-chip ob-c-mut">{t("editor.global")}</span>;

  const alege = (versiune: number) => {
    if (versiune === meta.version) { setDeschis(false); return; }
    if (!potPleca()) return;
    setDeschis(false);
    onChoose(versiune);
  };

  return (
    <div className="ob-vp" ref={cutie}>
      <button type="button" className={`ob-chip ob-vp-buton ${STARI[meta.status].chip}`}
              disabled={dezactivat} aria-expanded={deschis}
              title={t("editor.versiuni.titlu")}
              onClick={() => setDeschis((d) => !d)}>
        {t("editor.versiune", {
          versiune: meta.version, stare: t(STARI[meta.status].text),
        })}
      </button>
      {deschis && (
        // The height cap is inline so no stylesheet can lose it: a module can
        // have hundreds of versions, and this list must not take the page.
        <div className="ob-vp-panou" style={{ maxHeight: 320, overflowY: "auto" }}>
          <div className="ob-vp-titlu">{t("editor.versiuni.titlu")}</div>
          {eroare && <div className="err">{t("editor.versiuni.eroare")}</div>}
          {inOrdine(lista).map((v) => (
            <button key={v.version} type="button" className="ob-vp-rand"
                    data-versiune={v.version}
                    aria-current={v.version === meta.version ? "true" : undefined}
                    onClick={() => alege(v.version)}>
              <span className="ob-vp-cap">
                <b>v{v.version}</b>
                <span className={`ob-chip ${STARI[v.status].chip}`}>
                  {t(STARI[v.status].text)}
                </span>
                {v.version === meta.version && (
                  <span className="ob-vp-deschisa">{t("editor.versiuni.deschisa")}</span>
                )}
              </span>
              <span className="ob-vp-detalii">
                {[v.autor,
                  v.creat_la && dataOra(v.creat_la),
                  t(v.source === "person" ? "editor.versiuni.persoana"
                                          : "editor.versiuni.sistem"),
                  v.based_on != null && t("editor.versiuni.din", { v: v.based_on }),
                ].filter(Boolean).join(" · ")}
              </span>
              {v.nota && <span className="ob-vp-nota">{v.nota}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
